
import React, { FC, useEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import { Button } from '../../atoms/Button';
import { Link } from '../../atoms/Link';
import { RuleLine } from '../../atoms/RuleLine';
import { Icon } from '../../atoms/Icon';
import { Modal } from '../../molecules/Modal';
import { DropdownButtonProps } from './DropdownButton.types';
import { StyledDropdownButton, StyledDropdownModal } from './DropdownButton.styles';

// Use named rather than default exports.
export const DropdownButton: FC<DropdownButtonProps> = ( props: DropdownButtonProps ) => {
  const {
    buttonLabel,
    modalHeader,
    linkListItems,
    styleclass,
    trackingProperties,
  } = props;

  const [isCollapsed, setIsCollapsed] = useState('d-none');
  const [iconClass, setIconClass] = useState('icon-arrow-down');
  const [display, setDisplay] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [ariaLabel, setAriaLabel] = useState('ARIA_COLLAPSE');
  const [mQuery, setMQuery] = useState(true);
  const DropdownButtonRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    setDisplay(!display);
    handleScreenResize();
    window.addEventListener('resize', handleScreenResize);
  }, []);

  const handleScreenResize = () => {
    const mobileSize = window.matchMedia('(max-width: 991px)');
    const desktopSize = window.matchMedia('(min-width: 992px)');
    if (mobileSize.matches) {
      setMQuery(true);
    } else if (desktopSize.matches) {
      setMQuery(false);
    }
  };

  const buttonCallback = () => {
    setIsCollapsed(display ? 'd-block' : 'd-none');
    setIconClass(display ? 'icon-arrow-up' : 'icon-arrow-down');
    setAriaLabel(display ? 'ARIA_EXPAND' : 'ARIA_COLLAPSE');
    setShowModal(display ? true : false);
    setDisplay(!display);
  };

  const closeModal = () => {
    setShowModal(false);
    setIsCollapsed(display ? 'd-block' : 'd-none');
    setIconClass(display ? 'icon-arrow-up' : 'icon-arrow-down');
    setAriaLabel(display ? 'ARIA_EXPAND' : 'ARIA_COLLAPSE');
    setDisplay(!display);
  };

  const linkCallback = (e: { preventDefault: () => void; }, callbackfuntion: (() => void) | undefined) => {
    if (callbackfuntion) {
    e.preventDefault();
    callbackfuntion();
    }
    setIsCollapsed(display ? 'd-block' : 'd-none');
    setIconClass(display ? 'icon-arrow-up' : 'icon-arrow-down');
    setAriaLabel(display ? 'ARIA_EXPAND' : 'ARIA_COLLAPSE');
    setShowModal(false);
    setDisplay(!display);
  };

  const renderList = () => (
  <ul className="list-items">
    {linkListItems.map((item, index) => (
      <div key={'list-' + index}>
        <li>
          {item?.linkLabel && (
            <Link
              callback={(event) => linkCallback (event, item?.callBackFunction)}
              linkHref={item?.linkUrl}
              linkClassName={clsx(`list-item ${trackingProperties?.clickTrack ? 'custom_click_track' : ''}`)}
              custom_click_track_value={
                trackingProperties?.clickTrack
                  ? `${trackingProperties?.location}|${trackingProperties?.trackingDescription}|${
                    item.openInNewTab ? 'external' : 'internal'
                    }`
                  : ''
              }
              rel={item?.openInNewTab ? 'noopener noreferrer' : ''}
              target={item.openInNewTab ? '_blank' : '_self'}
            >{item?.linkLabel} <span className={"icon-arrow-right"}></span></Link>
          )}
        </li>
        <div className="rule-container">
          <RuleLine styleclass="t-horizontal-vertical-rule ruler-length-80" alignment="left"/>
        </div>
      </div>
    ))}
  </ul>
  );
  
  return (
    <StyledDropdownButton data-testid="ui-library-DropdownButton" data-component-name="m-ui-library-DropdownButton" className={clsx(styleclass ? styleclass : '')}>
      {buttonLabel && (
        <Button      
          className={`mb-2 bl_cta m-button-primary-icon m-button-s`}
          ariaLabel={buttonLabel}
          callback={buttonCallback}
          ref={DropdownButtonRef}
        >
          {buttonLabel}
          <Icon iconClass={clsx('pl-2 icon', `${iconClass}`)}>
            <span className="visually-hidden" aria-live="polite">{ariaLabel}</span>
          </Icon>
        </Button>
      )}
      {!mQuery && (
      <div className={clsx('list-container', isCollapsed )}>{renderList()}</div>
      )}
      {mQuery && (
      <StyledDropdownModal>
        <Modal
          show={showModal}
          className="drop-modal-container"
        >
          <Modal.Header
            labelText={modalHeader}
            popupHeaderOnCLoseFunc={closeModal}
            className="video-modal-header"
            popupCloseClassName="drop-modal-close-btn m-icon-button-primary"
          />
          <Modal.Body className="p-0 drop-modal-body">{renderList()}</Modal.Body>
        </Modal>
      </StyledDropdownModal>
      )}
    </StyledDropdownButton>
  );
};

